import { useState, useEffect } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';

import { RadioOptionsWithConfirm, IQuestion, InputWithConfirm, CardContainer, CardWithModal } from 'shared-components';

import { ApplicationState } from '../../../../../store';
import { saveVisaPrepForm } from '../../../../../store/student/actions';
import { createUpdateVPIFieldAction } from '../../../../../store/student/visaPrepForm/actions';
import QuestionPage from '../../../../templates/question';
import { VQ } from '../../../../../store/student/types';
import ExclusionOrders from './exclusionOrders';
import { IExclusionOrder } from '../../../../../types';
import { Pages } from '../../../../../store/student/visaPrepForm/types';
import { getPageStatus } from './utils';
import { visaPrepFlags } from '../../../../../utilities/pages';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from "react-i18next";

const VisaPrepFormAddHistoryPage4 = (_props: any) => {
    const { t } = useTranslation('common');
  // dispatch actions
  const dispatch = useDispatch();

  const onNext = () => {
    dispatch(saveVisaPrepForm(Pages.PAGE2_4));
  };

  const onChange = (fieldName: string, value: any, refValue?: any) =>
    dispatch(createUpdateVPIFieldAction(fieldName, value, refValue));

  const navigate = useNavigate();
  const onBackToTaskList = () => navigate(-1);

  // state props from store
  const student = useSelector(({ student }: ApplicationState) => student, shallowEqual);
  const {
    hasBeenToUk,
    hasNINumber,
    niNumber,
    hasReceivedMedicalTreatment,
    hasVoluntarilyDeparted,
    hasExclusionOrder,
    exclusionOrders = [],
  } = student.visa;

  const { hasTravelledToUk } = visaPrepFlags(student.personalInfo, student.offer, student.history);

  // constants
  const currentStep = 4;
  const steps = 8;

  // component state
  const [complete, setComplete] = useState(false);
  const [exclusionOrdersState, setExclusionOrdersState] = useState<IExclusionOrder[]>([]);
  const [showModal, setShowModal] = useState<boolean[]>([]);

  useEffect(() => {
    setExclusionOrdersState([
      ...exclusionOrders,
      {
        exclusionDate: '',
        reason: '',
        reference: '',
      },
    ]);
    setShowModal([...exclusionOrders.map(_ => false), false]);
  }, [exclusionOrders]);

  const toggleModal = (index: number) => {
    setShowModal(showModal.map((value, i) => (i === index ? !value : value)));
  };

  const deleteOrder = (index: number): void => {
    onChange(VQ.EXCLUSION_ORDERS, [...exclusionOrders.slice(0, index), ...exclusionOrders.slice(index + 1)]);
  };

  useEffect(() => {
    const { complete: newComplete } = getPageStatus({
      hasTravelledToUk,
      hasBeenToUk,
      hasNINumber,
      niNumber,
      hasReceivedMedicalTreatment,
      hasVoluntarilyDeparted,
      hasExclusionOrder,
      exclusionOrders,
    });
    if (newComplete !== complete) {
      setComplete(newComplete);
    }
  }, [
    complete,
    hasTravelledToUk,
    hasBeenToUk,
    hasNINumber,
    niNumber,
    hasReceivedMedicalTreatment,
    hasVoluntarilyDeparted,
    hasExclusionOrder,
    exclusionOrders,
  ]);

  const onHasBeenToUkClick = (question: IQuestion) => {
    onChange(VQ.HAS_BEEN_TO_UK, question.label === 'Yes', {
      hasTravelledToUk,
    });
  };

  const onHasNINumberClick = (question: IQuestion) => {
    onChange(VQ.HAS_NI_NUMBER, question.label === 'Yes');
  };

  const onHasReceivedMedicalTreatmentClick = (question: IQuestion) => {
    onChange(VQ.HAS_RECEIVED_MEDICAL_TREATMENT, question.label === 'Yes');
  };

  const onHasVoluntarilyDepartedClick = (question: IQuestion) => {
    onChange(VQ.HAS_VOLUNTARILY_DEPARTED, question.label === 'Yes');
  };

  const onHasExclusionOrderClick = (question: IQuestion) => {
    onChange(VQ.HAS_EXCLUSION_ORDER, question.label === 'Yes');
  };

  return (
    <QuestionPage
      complete={complete}
      currentStep={currentStep}
      steps={steps}
      onNext={onNext}
      onBackToTaskList={onBackToTaskList}
      section={'Additional History'}
    >
      <RadioOptionsWithConfirm
        key={VQ.HAS_BEEN_TO_UK}
        id={VQ.HAS_BEEN_TO_UK}
        label={t('common:haveYouEverBeenToTheUk')}
        onClick={(question: any) => onHasBeenToUkClick(question)}
        isInline={true}
        questions={[
          {
            char: 'Y',
            label: 'Yes',
            selected: hasBeenToUk === true,
          },
          {
            char: 'N',
            label: 'No',
            selected: hasBeenToUk === false,
          },
        ]}
      />

      {hasBeenToUk !== undefined && (hasBeenToUk === true || hasTravelledToUk === true) ? (
        <>
          <RadioOptionsWithConfirm
            key={VQ.HAS_NI_NUMBER}
            id={VQ.HAS_NI_NUMBER}
            label={t('common:doYouHaveAUkNationalInsuranceNum')}
            onClick={(question: any) => onHasNINumberClick(question)}
            isInline={true}
            questions={[
              {
                char: 'Y',
                label: 'Yes',
                selected: hasNINumber === true,
              },
              {
                char: 'N',
                label: 'No',
                selected: hasNINumber === false,
              },
            ]}
          />

          {hasNINumber === true ? (
            <InputWithConfirm
              key={VQ.NI_NUMBER}
              id={VQ.NI_NUMBER}
              value={niNumber}
              label={t('common:whatIsTheNumber')}
              onChange={(evt: any) => {
                onChange(VQ.NI_NUMBER, evt.currentTarget.value);
              }}
            />
          ) : null}

          <RadioOptionsWithConfirm
            key={VQ.HAS_RECEIVED_MEDICAL_TREATMENT}
            id={VQ.HAS_RECEIVED_MEDICAL_TREATMENT}
            label={t('common:haveYouEverReceivedMedicalTreatm')}
            onClick={(question: any) => onHasReceivedMedicalTreatmentClick(question)}
            isInline={true}
            questions={[
              {
                char: 'Y',
                label: 'Yes',
                selected: hasReceivedMedicalTreatment === true,
              },
              {
                char: 'N',
                label: 'No',
                selected: hasReceivedMedicalTreatment === false,
              },
            ]}
          />

          <RadioOptionsWithConfirm
            key={VQ.HAS_VOLUNTARILY_DEPARTED}
            id={VQ.HAS_VOLUNTARILY_DEPARTED}
            label={t('common:haveYouEverVoluntarilyElectedToD')}
            onClick={(question: any) => onHasVoluntarilyDepartedClick(question)}
            isInline={true}
            questions={[
              {
                char: 'Y',
                label: 'Yes',
                selected: hasVoluntarilyDeparted === true,
              },
              {
                char: 'N',
                label: 'No',
                selected: hasVoluntarilyDeparted === false,
              },
            ]}
          />

          <RadioOptionsWithConfirm
            key={VQ.HAS_EXCLUSION_ORDER}
            id={VQ.HAS_EXCLUSION_ORDER}
            label={t('common:haveYouReceivedOrHaveYouBeenSubj')}
            onClick={(question: any) => onHasExclusionOrderClick(question)}
            isInline={true}
            questions={[
              {
                char: 'Y',
                label: 'Yes',
                selected: hasExclusionOrder === true,
              },
              {
                char: 'N',
                label: 'No',
                selected: hasExclusionOrder === false,
              },
            ]}
          />

          {hasExclusionOrder === true ? (
            <CardContainer title={t('common:howManyExclusionOrdersHaveYouRec')}>
              {exclusionOrdersState.map((order: IExclusionOrder, index: number) => (
                <CardWithModal
                  key={index}
                  id={`exclusion-order-${index}`}
                  index={index}
                  modalTitle={'Add Exclusion Order'}
                  cardItems={[
                    {
                      label: 'Date of exclusion',
                      value: order.exclusionDate,
                    },
                    {
                      label: 'Reason',
                      value: order.reason,
                    },
                    {
                      label: 'Reference',
                      value: order.reference,
                    },
                  ]}
                  onRemove={deleteOrder}
                  addButtonLabel={`Add Exclusion Order`}
                  addAnotherButtonLabel={`I have another Exclusion Order to add`}
                  hasAddMoreButton={order.exclusionDate === '' && order.reason === '' && order.reference === ''}
                  showModal={showModal[index]}
                  toggleModal={toggleModal}
                >
                  <ExclusionOrders
                    index={index}
                    exclusionDate={order.exclusionDate}
                    reason={order.reason}
                    reference={order.reference}
                    onSaveAndClose={(order: IExclusionOrder) => {
                      const orders = [...exclusionOrders];
                      orders[index] = {
                        ...order,
                      };
                      onChange(VQ.EXCLUSION_ORDERS, orders);
                      toggleModal(index);
                    }}
                  />
                </CardWithModal>
              ))}
            </CardContainer>
          ) : null}
        </>
      ) : null}
    </QuestionPage>
  );
};
export default VisaPrepFormAddHistoryPage4;
